import React from "react";
import { useRef, useEffect, useId } from 'react';
import { IGatsbyImageData } from "gatsby-plugin-image";
import Swiper from 'swiper';
import { Pagination, Navigation, A11y, FreeMode } from 'swiper/modules';
import GatsbyImageTag from "components/partials/parts/gatsby-image-tag";
import { ReactComponent as CaretLeftIcon } from "img/arrows/caret-left.svg";
import { ReactComponent as CaretRightIcon } from "img/arrows/caret-right.svg";
import { SvgLogoImage } from "types/svg-logo-image";

/**
 * Logo Carousel Slide
 * Suggested image size: 200x127
 */
export type LogoCarouselSlide = {
    image?: IGatsbyImageData;
    svgLogoImage?: SvgLogoImage;
    url: string;
    alt?: string;
};

export type LogoCarouselProps = {
    headline: string;
    slides: LogoCarouselSlide[];
};

const LogoCarousel: React.FC<LogoCarouselProps> = ({ headline, slides }) => {

    const swiperRef = useRef(null);

    // ensures unique id for each carousel
    const id = useId().replaceAll(':', '');

    useEffect(() => {
        if (swiperRef.current !== null) {
            const swiperInstance = new Swiper(swiperRef.current, {
                modules: [Navigation, Pagination, A11y, FreeMode],
                slidesPerView: 2,
                spaceBetween: 20,
                centerInsufficientSlides: true,
                freeMode: true,
                watchSlidesProgress: true,
                navigation: {
                    nextEl: `.logo-carousel-swiper-${id} .swiper-nav-next`,
                    prevEl: `.logo-carousel-swiper-${id} .swiper-nav-prev`,
                },
                pagination: {
                    el: `.logo-carousel-swiper-${id} ~ .logo-swiper__pagination`,
                    clickable: true,
                },
                breakpoints: {
                    650: {
                        slidesPerView: 3,
                        spaceBetween: 30,
                    },
                    768: {
                        slidesPerView: 4,
                        spaceBetween: 40,
                    },
                    1024: {
                        slidesPerView: 5,
                        spaceBetween: 50,
                    }

                }
            });

            if (process.env.GATSBY_SERVER_ENV === 'dev') {
                // Attach the swiper instance to the window object
                // for debugging purposes.
                // you will need to review the rendered html to find the correct id for 
                // carousel-wrapper-${id} and replace XXX with the id.
                // console commands:
                // window.swiperLogoCarouselCardsXXX.slideNext();
                // window.swiperLogoCarouselCardsXXX.slidePrev();
                (window as any)[`swiperLogoCarouselCards${id}`] = swiperInstance;
            }

        }
    }, []);

    return (
        <div className="pattern pattern--logo-carousel">
            <div className="container">
                <h2 className="pattern__headline text-center">{headline}</h2>
                <div className={`logo-carousel-swiper logo-carousel-swiper-${id}`}>
                    <div className="swiper js-logo-swiper" ref={swiperRef}>
                        <div className="swiper-wrapper">
                            {slides.map((slide, index) => (
                                <div className="swiper-slide" key={index}>
                                    <a href={slide.url}>
                                        <GatsbyImageTag image={slide.image} svgLogoImage={slide.svgLogoImage} alt={slide.alt} />
                                    </a>
                                </div>
                            ))}
                        </div>
                    </div>
                    <div className="swiper-nav-prev btn-circle-arrow">
                        <CaretLeftIcon />
                    </div>
                    <div className="swiper-nav-next btn-circle-arrow">
                        <CaretRightIcon />
                    </div>
                </div>
                <div className="logo-swiper__pagination swiper-pagination"></div>
            </div>
        </div>
    );
};

export default LogoCarousel;